import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { extractErrorMessage } from '../../core/services/api';
import {
  llmAgent,
  resources,
  type LlmJobState,
  type LlmJobStatus,
} from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import type { LineOfBizPersona } from '../../core/types/data-models';

/**
 * Shape of `result` returned by `generatePersonaAction`. The prompt's
 * "Output format" is wrapped under a `personas` key — the agent's
 * structured-outputs path requires an `object` root, so the action's
 * OUTPUT_SHAPE has to be an object and the prompt sample is matched
 * to it.
 */
interface PersonaSuggestion {
  persona: string;
  description?: string;
  /** Relative importance 0–100 (maps to LineOfBizPersona.promptsWeight). */
  weight?: number;
}

interface PersonasResult {
  personas?: PersonaSuggestion[];
}

interface GeneratePersonasDialogProps {
  open: boolean;
  lineOfBizId: number | null;
  onClose: () => void;
}

const TERMINAL_STATES: ReadonlySet<LlmJobState> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

/**
 * Dialog that drives the generate-persona flow:
 *
 *   1. POST /line-of-biz-personas/generate-persona/:lineOfBizId to
 *      start a job.
 *   2. Poll GET /llm-agent/jobs/:jobId every 1.5s until terminal.
 *   3. Render the result with one checkbox per suggested persona; the
 *      user picks which to save and clicks Save.
 *   4. For each picked persona, create one LineOfBizPersona row.
 *      `commonPersonaId` is left null — the LLM can't map to a global
 *      catalog entry, the user can link one later via the row's edit
 *      dialog if they want to.
 */
export function GeneratePersonasDialog({
  open,
  lineOfBizId,
  onClose,
}: GeneratePersonasDialogProps) {
  const { activeTenantId } = useTenant();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<LlmJobStatus<PersonasResult> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const jobIdRef = useRef<string | null>(null);

  // ----- start job ----------------------------------------------------
  const start = useMutation({
    mutationFn: (id: number) => resources.generateLineOfBizPersona(id),
    onSuccess: (data) => {
      jobIdRef.current = data.jobId;
      setStatus(data.status as LlmJobStatus<PersonasResult>);
    },
    onError: (err) => setStartError(extractErrorMessage(err)),
  });

  // ----- save selection ----------------------------------------------
  // Each picked persona becomes a fresh LineOfBizPersona row. We don't
  // try to dedupe against existing personas — the user is reviewing
  // the list and can drop suggestions they already have.
  const save = useMutation({
    mutationFn: async (picks: PersonaSuggestion[]) => {
      if (lineOfBizId == null) return;
      for (const pick of picks) {
        const name = pick.persona.trim();
        if (!name) continue;
        await resources.lineOfBizPersonas.create({
          lineOfBizId,
          name,
          description: pick.description?.trim() || null,
          commonPersonaId: null,
          promptsWeight: clampWeight(pick.weight),
          ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
        } as Partial<LineOfBizPersona>);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['line-of-biz-personas'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const retry = () => {
    if (lineOfBizId == null) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    start.mutate(lineOfBizId);
  };

  // ----- open / reset / start -----------------------------------------
  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setError(null);
    setStartError(null);
    setSelected(new Set());
    jobIdRef.current = null;
    if (lineOfBizId != null) start.mutate(lineOfBizId);
    // start.mutate identity is stable for the mutation hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lineOfBizId]);

  // ----- polling loop -------------------------------------------------
  useEffect(() => {
    if (!open) return;
    if (status == null) return;
    if (TERMINAL_STATES.has(status.state)) return;
    let cancelled = false;
    const tick = async () => {
      const jobId = jobIdRef.current;
      if (!jobId) return;
      try {
        const next = await llmAgent.getJob<PersonasResult>(jobId);
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      }
    };
    const handle = window.setInterval(tick, 1500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, status]);

  // Default selection — once a job completes, preselect every persona
  // so Save is one click for the common "looks good" case.
  useEffect(() => {
    if (status?.state !== 'completed') return;
    const personas = status.result?.personas ?? [];
    setSelected(new Set(personas.map((_, idx) => idx)));
  }, [status]);

  const personas = status?.result?.personas ?? [];
  const lastStep = status?.steps?.[status.steps.length - 1]?.step;

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

  const isCompleted = status?.state === 'completed';
  const isRunning =
    start.isPending || (status != null && !TERMINAL_STATES.has(status.state));
  const canSave = isCompleted && selected.size > 0 && !save.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setError(null);
    const picks = personas.filter((_, idx) => selected.has(idx));
    await save.mutateAsync(picks);
  };

  return (
    <FormDialog
      open={open}
      title="Generate personas"
      submitLabel={save.isPending ? 'Saving…' : 'Save selected'}
      loading={save.isPending}
      submitDisabled={!canSave}
      maxWidth="lg"
      onSubmit={handleSubmit}
      onClose={() => {
        if (save.isPending) return;
        onClose();
      }}
    >
      {error && <Alert severity="error">{error}</Alert>}

      {/* ---- Job progress ---- */}
      {isRunning && (
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {status?.state === 'pending' || status == null
                ? 'Starting…'
                : lastStep ?? 'Researching buyer personas…'}
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              status?.progress != null && status.progress > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              status?.progress != null
                ? Math.round(status.progress * 100)
                : undefined
            }
          />
        </Stack>
      )}

      {status?.state === 'failed' && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {status.error || 'The persona-research job failed.'}
        </Alert>
      )}
      {status?.state === 'cancelled' && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          The persona-research job was cancelled.
        </Alert>
      )}
      {startError && (
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              color="inherit"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={retry}
              disabled={start.isPending}
            >
              Retry
            </Button>
          }
        >
          {startError}
        </Alert>
      )}

      {/* ---- Results review ---- */}
      {isCompleted && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {personas.length === 0 ? (
            <Alert severity="warning">
              The analyzer returned no personas.
            </Alert>
          ) : (
            <Stack spacing={1}>
              {personas.map((p, idx) => (
                <Paper
                  key={`${p.persona}-${idx}`}
                  variant="outlined"
                  sx={{ p: 1.5 }}
                >
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Checkbox
                      checked={selected.has(idx)}
                      onChange={() => toggle(idx)}
                      sx={{ p: 0.5, mt: -0.25 }}
                    />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, minWidth: 0 }}>
                          {p.persona}
                        </Typography>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`Weight ${clampWeight(p.weight)}%`}
                        />
                      </Stack>
                      {p.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {p.description}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}

/** Coerce the model's weight into a valid 0–100 integer (0 when missing). */
function clampWeight(w: number | undefined): number {
  if (typeof w !== 'number' || !Number.isFinite(w)) return 0;
  return Math.max(0, Math.min(100, Math.round(w)));
}
